import { ObjectSerializer } from "../Serialization/ObjectSerializer";
import { ObjSerUtils } from "../Serialization/ObjSerUtils";
import { Guid } from "../Utils/Guid";
import { HashSet } from "../Utils/HashSet";
import { ObjUtils } from "../Utils/ObjUtils";
import { ValueEncoder } from "../Utils/ValueEncoder";

export class Reason {
	public Value: string;
}

export class SchedDiscPrtgExport implements ObjectSerializer {

	public static readonly InvalidPrtgId = -2147483648;

	public EnableExport: boolean;
	public ExportOrigin: string;
	public ServerSettings: Guid;
	public ServerSettings2: Guid;
	public ServerSettings3: Guid;
	public ServerSettings4: Guid;
	public ParentGroupName: string;
	public ParentGroupId: number;
	public GroupName: string;
	public GroupId: number;
	public RemoveMissingDevices: boolean;
	public ExportMapsOnly: boolean;
	public MergeMapsOnExport: boolean;
	public ExportMasterNetworkOnly: boolean;
	public AddDeviceDependency: boolean;
	public AddPingMonitors: boolean;
	public AddSnmpUpTimeMonitors: boolean;
	public AddSnmpCpuMonitors: boolean;
	public AddSnmpMemoryMonitors: boolean;
	public AddSnmpDiskFreeMonitors: boolean;
	public AddSnmpIfMonitors: boolean;
	public AddSnmpIfMonitors_AllUp: boolean;
	public AddSnmpIfMonitors_AllPhysical: boolean;
	public AddSnmpIfMonitors_AllUplinks: boolean;
	public AddWmiUpTimeMonitors: boolean;
	public AddWmiCpuMonitors: boolean;
	public AddWmiDiskSpaceMonitors: boolean;
	public AddWmiMemoryMonitors: boolean;
	public UseTemplates: boolean;
	public ExportMaps: boolean;
	public DeviceCategories: HashSet<Guid>;
	public DeviceGroups: HashSet<Guid>;

	public get HasServerSettings(): boolean {
		return !!this.ServerSettings;
	}

	public get HasServerSettings2(): boolean {
		return !!this.ServerSettings2;
	}

	public get HasServerSettings3(): boolean {
		return !!this.ServerSettings3;
	}

	public get HasServerSettings4(): boolean {
		return !!this.ServerSettings4;
	}

	public get HasExportOrigin(): boolean {
		return !!this.ExportOrigin;
	}

	public get HasParentGroupName(): boolean {
		return !!this.ParentGroupName;
	}

	public get HasParentGroupId(): boolean {
		return (this.ParentGroupId != SchedDiscPrtgExport.InvalidPrtgId);
	}

	public get HasGroupName(): boolean {
		return !!this.GroupName;
	}

	public get HasGroupId(): boolean {
		return (this.GroupId != SchedDiscPrtgExport.InvalidPrtgId);
	}

	public get HasDeviceCategories(): boolean {
		return (this.DeviceCategories && this.DeviceCategories.Count > 0);
	}

	public get HasDeviceGroups(): boolean {
		return (this.DeviceGroups && this.DeviceGroups.Count > 0);
	}

	constructor() {
		this.Clear();
	}

	public Copy(other: SchedDiscPrtgExport): void {
		this.Clear();

		this.EnableExport = other.EnableExport;
		this.ExportOrigin = other.ExportOrigin;
		this.ServerSettings = other.ServerSettings;
		this.ServerSettings2 = other.ServerSettings2;
		this.ServerSettings3 = other.ServerSettings3;
		this.ServerSettings4 = other.ServerSettings4;
		this.ParentGroupName = other.ParentGroupName;
		this.ParentGroupId = other.ParentGroupId;
		this.GroupName = other.GroupName;
		this.GroupId = other.GroupId;
		this.RemoveMissingDevices = other.RemoveMissingDevices;
		this.ExportMapsOnly = other.ExportMapsOnly;
		this.MergeMapsOnExport = other.MergeMapsOnExport;
		this.ExportMasterNetworkOnly = other.ExportMasterNetworkOnly;
		this.AddDeviceDependency = other.AddDeviceDependency;
		this.AddPingMonitors = other.AddPingMonitors;
		this.AddSnmpUpTimeMonitors = other.AddSnmpUpTimeMonitors;
		this.AddSnmpCpuMonitors = other.AddSnmpCpuMonitors;
		this.AddSnmpMemoryMonitors = other.AddSnmpMemoryMonitors;
		this.AddSnmpDiskFreeMonitors = other.AddSnmpDiskFreeMonitors;
		this.AddSnmpIfMonitors = other.AddSnmpIfMonitors;
		this.AddSnmpIfMonitors_AllUp = other.AddSnmpIfMonitors_AllUp;
		this.AddSnmpIfMonitors_AllPhysical = other.AddSnmpIfMonitors_AllPhysical;
		this.AddSnmpIfMonitors_AllUplinks = other.AddSnmpIfMonitors_AllUplinks;
		this.AddWmiUpTimeMonitors = other.AddWmiUpTimeMonitors;
		this.AddWmiCpuMonitors = other.AddWmiCpuMonitors;
		this.AddWmiMemoryMonitors = other.AddWmiMemoryMonitors;
		this.AddWmiDiskSpaceMonitors = other.AddWmiDiskSpaceMonitors;
		this.UseTemplates = other.UseTemplates;
		this.ExportMaps = other.ExportMaps;
		this.DeviceCategories.UnionWith(other.DeviceCategories);
		this.DeviceGroups.UnionWith(other.DeviceGroups);
	}

	public Clear(): void {
		this.EnableExport = false;
		this.ExportOrigin = "agent";
		this.ServerSettings = null;
		this.ServerSettings2 = null;
		this.ServerSettings3 = null;
		this.ServerSettings4 = null;
		this.ParentGroupName = "";
		this.ParentGroupId = SchedDiscPrtgExport.InvalidPrtgId;
		this.GroupName = "";
		this.GroupId = SchedDiscPrtgExport.InvalidPrtgId;
		this.RemoveMissingDevices = false;
		this.ExportMapsOnly = false;
		this.MergeMapsOnExport = false;
		this.ExportMasterNetworkOnly = false;
		this.AddDeviceDependency = false;
		this.AddPingMonitors = true;
		this.AddSnmpUpTimeMonitors = false;
		this.AddSnmpCpuMonitors = false;
		this.AddSnmpMemoryMonitors = false;
		this.AddSnmpDiskFreeMonitors = false;
		this.AddSnmpIfMonitors = false;
		this.AddSnmpIfMonitors_AllUp = false;
		this.AddSnmpIfMonitors_AllPhysical = false;
		this.AddSnmpIfMonitors_AllUplinks = false;
		this.AddWmiUpTimeMonitors = false;
		this.AddWmiCpuMonitors = false;
		this.AddWmiMemoryMonitors = false;
		this.AddWmiDiskSpaceMonitors = false;
		this.ExportMaps = false;
		this.UseTemplates = false;

		if (!this.DeviceCategories)
			this.DeviceCategories = new HashSet<Guid>();
		else
			this.DeviceCategories.Clear();

		if (!this.DeviceGroups)
			this.DeviceGroups = new HashSet<Guid>();
		else
			this.DeviceGroups.Clear();
	}

	public Equals(obj: any): boolean {
		if (!obj)
			return false;

		if (!(obj instanceof SchedDiscPrtgExport))
			return false;
		var other = (obj as SchedDiscPrtgExport);

		return (this.EnableExport == other.EnableExport &&
			this.ExportOrigin == other.ExportOrigin &&
			ObjUtils.Equals(this.ServerSettings, other.ServerSettings) &&
			ObjUtils.Equals(this.ServerSettings2, other.ServerSettings2) &&
			ObjUtils.Equals(this.ServerSettings3, other.ServerSettings3) &&
			ObjUtils.Equals(this.ServerSettings4, other.ServerSettings4) &&
			this.ParentGroupName == other.ParentGroupName &&
			this.ParentGroupId == other.ParentGroupId &&
			this.GroupName == other.GroupName &&
			this.GroupId == other.GroupId &&
			this.RemoveMissingDevices == other.RemoveMissingDevices &&
			this.ExportMapsOnly == other.ExportMapsOnly &&
			this.MergeMapsOnExport == other.MergeMapsOnExport &&
			this.ExportMasterNetworkOnly == other.ExportMasterNetworkOnly &&
			this.AddDeviceDependency == other.AddDeviceDependency &&
			this.AddPingMonitors == other.AddPingMonitors &&
			this.AddSnmpUpTimeMonitors == other.AddSnmpUpTimeMonitors &&
			this.AddSnmpCpuMonitors == other.AddSnmpCpuMonitors &&
			this.AddSnmpMemoryMonitors == other.AddSnmpMemoryMonitors &&
			this.AddSnmpDiskFreeMonitors == other.AddSnmpDiskFreeMonitors &&
			this.AddSnmpIfMonitors == other.AddSnmpIfMonitors &&
			this.AddSnmpIfMonitors_AllUp == other.AddSnmpIfMonitors_AllUp &&
			this.AddSnmpIfMonitors_AllPhysical == other.AddSnmpIfMonitors_AllPhysical &&
			this.AddSnmpIfMonitors_AllUplinks == other.AddSnmpIfMonitors_AllUplinks &&
			this.AddWmiUpTimeMonitors == other.AddWmiUpTimeMonitors &&
			this.AddWmiCpuMonitors == other.AddWmiCpuMonitors &&
			this.AddWmiMemoryMonitors == other.AddWmiMemoryMonitors &&
			this.AddWmiDiskSpaceMonitors == other.AddWmiDiskSpaceMonitors &&
			this.UseTemplates == other.UseTemplates &&
			this.ExportMaps == other.ExportMaps &&
			ObjUtils.SetEquals(this.DeviceCategories, other.DeviceCategories) &&
			ObjUtils.SetEquals(this.DeviceGroups, other.DeviceGroups));
	}

	public IsValid(reason: Reason): boolean {
		reason.Value = "";

		if (this.EnableExport) {
			if (!this.HasServerSettings) {
				reason.Value = "PRTG server settings must be selected";
				return false;
			}
			if (!this.HasGroupName) {
				reason.Value = "A PRTG device group name must be specified";
				return false;
			}

			return true;
		}
		else {
			return true;
		}
	}

	public Write(obj: any, context: any): void {
		obj.sched_disc_prtg_export = {};
		obj = obj.sched_disc_prtg_export;

		obj.enable_export = ValueEncoder.EncodeBoolean(this.EnableExport);

		if (this.HasExportOrigin)
			obj.export_origin = this.ExportOrigin;
		if (this.HasServerSettings)
			obj.server_settings = ValueEncoder.EncodeGuid(this.ServerSettings);
		if (this.HasServerSettings2)
			obj.server_settings_2 = ValueEncoder.EncodeGuid(this.ServerSettings2);
		if (this.HasServerSettings3)
			obj.server_settings_3 = ValueEncoder.EncodeGuid(this.ServerSettings3);
		if (this.HasServerSettings4)
			obj.server_settings_4 = ValueEncoder.EncodeGuid(this.ServerSettings4);
		if (this.HasParentGroupName)
			obj.parent_group_name = this.ParentGroupName;
		if (this.HasParentGroupId)
			obj.parent_group_id = ValueEncoder.EncodeInteger(this.ParentGroupId);
		if (this.HasGroupName)
			obj.group_name = this.GroupName;
		if (this.HasGroupId)
			obj.group_id = ValueEncoder.EncodeInteger(this.GroupId);
		obj.remove_missing_devices = ValueEncoder.EncodeBoolean(this.RemoveMissingDevices);
		obj.export_maps_only = ValueEncoder.EncodeBoolean(this.ExportMapsOnly);
		obj.merge_maps_on_export = ValueEncoder.EncodeBoolean(this.MergeMapsOnExport);
		obj.export_master_only = ValueEncoder.EncodeBoolean(this.ExportMasterNetworkOnly);

		obj.add_device_dependency = ValueEncoder.EncodeBoolean(this.AddDeviceDependency);
		obj.add_ping_monitors = ValueEncoder.EncodeBoolean(this.AddPingMonitors);
		obj.add_snmp_uptime_monitors = ValueEncoder.EncodeBoolean(this.AddSnmpUpTimeMonitors);
		obj.add_snmp_cpu_monitors = ValueEncoder.EncodeBoolean(this.AddSnmpCpuMonitors);
		obj.add_snmp_memory_monitors = ValueEncoder.EncodeBoolean(this.AddSnmpMemoryMonitors);
		obj.add_snmp_diskfree_monitors = ValueEncoder.EncodeBoolean(this.AddSnmpDiskFreeMonitors);
		obj.add_snmp_if_monitors = ValueEncoder.EncodeBoolean(this.AddSnmpIfMonitors);
		obj.add_snmp_if_monitors_allup = ValueEncoder.EncodeBoolean(this.AddSnmpIfMonitors_AllUp);
		obj.add_snmp_if_monitors_allphysical = ValueEncoder.EncodeBoolean(this.AddSnmpIfMonitors_AllPhysical);
		obj.add_snmp_if_monitors_alluplinks = ValueEncoder.EncodeBoolean(this.AddSnmpIfMonitors_AllUplinks);
		obj.add_wmi_uptime_monitors = ValueEncoder.EncodeBoolean(this.AddWmiUpTimeMonitors);
		obj.add_wmi_cpu_monitors = ValueEncoder.EncodeBoolean(this.AddWmiCpuMonitors);
		obj.add_wmi_memory_monitors = ValueEncoder.EncodeBoolean(this.AddWmiMemoryMonitors);
		obj.add_wmi_disk_space_monitors = ValueEncoder.EncodeBoolean(this.AddWmiDiskSpaceMonitors);
		obj.use_templates = ValueEncoder.EncodeBoolean(this.UseTemplates);
		obj.export_maps = ValueEncoder.EncodeBoolean(this.ExportMaps);

		if (this.HasDeviceCategories) {
			obj.device_categories = [];
			ObjSerUtils.WriteCollection(obj.device_categories, this.DeviceCategories, (value: Guid) => {
				return { guid: ValueEncoder.EncodeGuid(value) };
			}, {});
		}

		if (this.HasDeviceGroups) {
			obj.device_groups = [];
			ObjSerUtils.WriteCollection(obj.device_groups, this.DeviceGroups, (value: Guid) => {
				return { guid: ValueEncoder.EncodeGuid(value) };
			}, {});
		}
	}

	public Read(obj: any, context: any): void {
		this.Clear();

		if (!obj.sched_disc_prtg_export)
			throw new Error();
		obj = obj.sched_disc_prtg_export;

		if (obj.enable_export !== undefined)
			this.EnableExport = ValueEncoder.DecodeBoolean(obj.enable_export);

		if (obj.export_origin !== undefined)
			this.ExportOrigin = obj.export_origin;

		if (obj.server_settings !== undefined)
			this.ServerSettings = ValueEncoder.DecodeGuid(obj.server_settings);

		if (obj.server_settings_2 !== undefined)
			this.ServerSettings2 = ValueEncoder.DecodeGuid(obj.server_settings_2);

		if (obj.server_settings_3 !== undefined)
			this.ServerSettings3 = ValueEncoder.DecodeGuid(obj.server_settings_3);

		if (obj.server_settings_4 !== undefined)
			this.ServerSettings4 = ValueEncoder.DecodeGuid(obj.server_settings_4);

		if (obj.parent_group_name !== undefined)
			this.ParentGroupName = obj.parent_group_name;

		if (obj.parent_group_id !== undefined)
			this.ParentGroupId = ValueEncoder.DecodeInteger(obj.parent_group_id);

		if (obj.group_name !== undefined)
			this.GroupName = obj.group_name;

		if (obj.group_id !== undefined)
			this.GroupId = ValueEncoder.DecodeInteger(obj.group_id);

		if (obj.remove_missing_devices !== undefined)
			this.RemoveMissingDevices = ValueEncoder.DecodeBoolean(obj.remove_missing_devices);

		if (obj.export_maps_only !== undefined)
			this.ExportMapsOnly = ValueEncoder.DecodeBoolean(obj.export_maps_only);

		if (obj.merge_maps_on_export !== undefined)
			this.MergeMapsOnExport = ValueEncoder.DecodeBoolean(obj.merge_maps_on_export);

		if (obj.export_master_only !== undefined)
			this.ExportMasterNetworkOnly = ValueEncoder.DecodeBoolean(obj.export_master_only);

		if (obj.add_device_dependency !== undefined)
			this.AddDeviceDependency = ValueEncoder.DecodeBoolean(obj.add_device_dependency);

		if (obj.add_ping_monitors !== undefined)
			this.AddPingMonitors = ValueEncoder.DecodeBoolean(obj.add_ping_monitors);

		if (obj.add_snmp_uptime_monitors !== undefined)
			this.AddSnmpUpTimeMonitors = ValueEncoder.DecodeBoolean(obj.add_snmp_uptime_monitors);

		if (obj.add_snmp_cpu_monitors !== undefined)
			this.AddSnmpCpuMonitors = ValueEncoder.DecodeBoolean(obj.add_snmp_cpu_monitors);

		if (obj.add_snmp_memory_monitors !== undefined)
			this.AddSnmpMemoryMonitors = ValueEncoder.DecodeBoolean(obj.add_snmp_memory_monitors);

		if (obj.add_snmp_diskfree_monitors !== undefined)
			this.AddSnmpDiskFreeMonitors = ValueEncoder.DecodeBoolean(obj.add_snmp_diskfree_monitors);

		if (obj.add_snmp_if_monitors !== undefined)
			this.AddSnmpIfMonitors = ValueEncoder.DecodeBoolean(obj.add_snmp_if_monitors);

		if (obj.add_snmp_if_monitors_allup !== undefined)
			this.AddSnmpIfMonitors_AllUp = ValueEncoder.DecodeBoolean(obj.add_snmp_if_monitors_allup);

		if (obj.add_snmp_if_monitors_allphysical !== undefined)
			this.AddSnmpIfMonitors_AllPhysical = ValueEncoder.DecodeBoolean(obj.add_snmp_if_monitors_allphysical);

		if (obj.add_snmp_if_monitors_alluplinks !== undefined)
			this.AddSnmpIfMonitors_AllUplinks = ValueEncoder.DecodeBoolean(obj.add_snmp_if_monitors_alluplinks);

		if (obj.add_wmi_uptime_monitors !== undefined)
			this.AddWmiUpTimeMonitors = ValueEncoder.DecodeBoolean(obj.add_wmi_uptime_monitors);

		if (obj.add_wmi_cpu_monitors !== undefined)
			this.AddWmiCpuMonitors = ValueEncoder.DecodeBoolean(obj.add_wmi_cpu_monitors);

		if (obj.add_wmi_memory_monitors !== undefined)
			this.AddWmiMemoryMonitors = ValueEncoder.DecodeBoolean(obj.add_wmi_memory_monitors);

		if (obj.add_wmi_disk_space_monitors !== undefined)
			this.AddWmiDiskSpaceMonitors = ValueEncoder.DecodeBoolean(obj.add_wmi_disk_space_monitors);

		if (obj.use_templates !== undefined)
			this.UseTemplates = ValueEncoder.DecodeBoolean(obj.use_templates);

		if (obj.export_maps !== undefined)
			this.ExportMaps = ValueEncoder.DecodeBoolean(obj.export_maps);

		if (obj.device_categories !== undefined)
			ObjSerUtils.ReadCollection(obj.device_categories, this.DeviceCategories, (value: any) => {
				return ValueEncoder.DecodeGuid(value.guid);
			}, {});

		if (obj.device_groups !== undefined)
			ObjSerUtils.ReadCollection(obj.device_groups, this.DeviceGroups, (value: any) => {
				return ValueEncoder.DecodeGuid(value.guid);
			}, {});
	}

}